/**
 * Login page — username and password form (MFA step 1).
 *
 * On successful credential validation the BFF sends a 6-digit login
 * code to the user's email.  The client stores the returned userId in
 * sessionStorage and navigates to the login-with-code page.
 *
 * @module pages/login
 */

import React, { useState } from 'react';
import { useNavigate,
         Link }            from 'react-router-dom';
import { Eye, EyeOff }    from 'lucide-react';
import Layout      from '../components/Layout';
import { useI18n } from '../dataProviders/I18nProvider';
import apiClient   from '../procedural/apiClient';
import config      from '../config';
import * as constants from '../constants';

/**
 * Renders the Login form with username, password (toggle-visible),
 * submit button, and "I forgot my password" link.
 *
 * @returns {React.ReactElement}
 */
export default function Login() {
  const { t }    = useI18n();
  const navigate = useNavigate();

  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState(null);
  const [submitting, setSubmitting]     = useState(false);

  /**
   * Submit credentials to POST /auth/login.
   *
   * @param {React.FormEvent} e - Form submit event.
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError(t('login.error.missingFields'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post('/auth/login', { username, password });
      sessionStorage.setItem(constants.SK_LOGIN_USER_ID, res.data.userId);
      sessionStorage.setItem(constants.SK_LOGIN_EMAIL_ID, res.data.emailId);
      navigate(config.pagePaths.loginWithCode);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'invalidCredentials') {
        setError(t('login.error.invalidCredentials'));
      } else if (errorCode === 'noEmail') {
        setError(t('login.error.noEmail'));
      } else {
        setError(t('login.error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout title={t('page.login')}>
      <div className="auth-container">
        <div className="auth-card">
          <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="auth-error">{error}</div>}

            <div className="auth-field">
              <label className="auth-label" htmlFor="login-username">
                {t('login.username.label')}
              </label>
              <input
                id="login-username"
                className="auth-input"
                type="text"
                autoComplete="username"
                maxLength={constants.MAX_EMAIL_LENGTH}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="login-password">
                {t('login.password.label')}
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="login-password"
                  className="auth-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  maxLength={constants.MAX_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="auth-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit"
              disabled={submitting}
            >
              {t('login.submit')}
            </button>

            <div className="auth-link">
              <Link to={config.pagePaths.changePassword}>
                {t('login.forgotPassword')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
